import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User } from '../types/User';
import bugsnagClient from '../config/bugsnag';
import { onSessionExpired, markSessionActive } from '../auth/sessionExpiry';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  devLogin: (email: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

// Create a default context value to avoid null checks
const defaultAuthContext: AuthContextType = {
  currentUser: null,
  loading: true,
  error: null,
  login: () => { authService.loginWithMicrosoft(); },
  devLogin: async () => false,
  logout: async () => { console.error('AuthContext not initialized'); },
  isAuthenticated: false
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authService.checkAuthStatus();
        if (response.authenticated && response.user) {
          setCurrentUser(response.user);
          setIsAuthenticated(true);
          markSessionActive();
        } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setCurrentUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // React to a 401 from any request made anywhere in the app. The HTTP handlers installed in
  // index.tsx funnel every 401 (other than the auth routes themselves) into this signal, which
  // is the only thing that re-evaluates authentication after the initial boot check. Clearing
  // isAuthenticated lets the existing ProtectedRoute guard redirect to /login.
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      console.warn('[auth] session expired - a request returned 401; signing out');
      setCurrentUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Update Bugsnag user context whenever currentUser changes
  useEffect(() => {
    if (currentUser) {
      bugsnagClient.setUser(
        currentUser.id.toString(),
        currentUser.email,
        currentUser.name || ''
      );
    } else {
      bugsnagClient.setUser();
    }
  }, [currentUser]);

  const login = () => {
    authService.loginWithMicrosoft();
  };

  // DEV ONLY: establish a session as the given user email (no password).
  const devLogin = async (email: string): Promise<boolean> => {
    try {
      const response = await authService.devLogin(email);
      if (response.authenticated && response.user) {
        setCurrentUser(response.user);
        setIsAuthenticated(true);
        setError(null);
        markSessionActive();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Dev login failed:', err);
      return false;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
      // Bugsnag user context will be cleared automatically via useEffect when currentUser becomes null
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed');
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    devLogin,
    logout,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 